import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import Modal from "@/components/ui/modal";
import Button from "@/components/ui/button";
import Input from "@/components/ui/input";
import Dropdown from "@/components/ui/dropdown";
import {
  Close as CloseIcon,
  AddEmployee as AddEmployeeIcon,
} from "@/components/icons";
import { staffManagementApi } from "../services/staff-management.api";
import { maskTurkishPhone } from "../utils/phoneMask";
import { rolesAndPermissionsApi } from "@/features/roles-and-permissions/services/roles-and-permissions.api";
import type { AdminUserCreateBody } from "@/lib/users.api";
import styles from "./EditStaffModal.module.scss";

// Gender labelleri t() ile getirilmek üzere component içinde useMemo ile
// üretiliyor — module-level array hardcoded TR oluyor ve EN modunda da
// "Erkek/Kadın" görünüyordu.

export interface AddStaffFormData {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  email: string;
  password: string;
  identityNumber: string;
  title: string;
  /** Personel sicil numarası */
  sicilNo: string;
  ldapUsername: string;
  locationId: string | null;
  departmentId: string | null;
  roleId: string | null;
  gender: "male" | "female" | null;
}

const getInitialFormData = (): AddStaffFormData => ({
  firstName: "",
  lastName: "",
  phoneNumber: "",
  email: "",
  password: "",
  identityNumber: "",
  title: "",
  sicilNo: "",
  ldapUsername: "",
  locationId: null,
  departmentId: null,
  roleId: null,
  gender: null,
});

const normalizePhone = (raw: string): string | null => {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const digits = trimmed.replace(/\D/g, "");
  if (digits.length === 10 && digits.startsWith("5")) {
    return `+90${digits}`;
  }
  if (digits.length === 11 && digits.startsWith("90")) {
    return `+${digits}`;
  }
  if (trimmed.startsWith("+90") || trimmed.startsWith("90")) {
    return trimmed.startsWith("+") ? trimmed : `+${trimmed}`;
  }
  return trimmed.startsWith("+") ? trimmed : `+90${digits}`;
};

interface AddStaffModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const AddStaffModal: React.FC<AddStaffModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const GENDER_OPTIONS = useMemo(
    () => [
      { label: t("staffManagement.gender.male"), value: "male" },
      { label: t("staffManagement.gender.female"), value: "female" },
      {
        label: t("staffManagement.gender.unspecified"),
        value: "",
      },
    ],
    [t],
  );
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] =
    useState<AddStaffFormData>(getInitialFormData());
  const [departmentOptions, setDepartmentOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [roleOptions, setRoleOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingInitial, setIsLoadingInitial] = useState(false);
  const [departmentsLoadFailed, setDepartmentsLoadFailed] = useState(false);
  const [rolesLoadFailed, setRolesLoadFailed] = useState(false);
  const [showValidation, setShowValidation] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setFormData(getInitialFormData());
      setCurrentStep(0);
      setShowValidation(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    setIsLoadingInitial(true);
    setDepartmentsLoadFailed(false);
    setRolesLoadFailed(false);
    Promise.allSettled([
      staffManagementApi.getDepartments(),
      rolesAndPermissionsApi.getAll({ pageSize: 100 }),
    ])
      .then(([depRes, rolesRes]) => {
        if (depRes.status === "fulfilled") {
          setDepartmentOptions(
            depRes.value.map((d) => ({ label: d.name, value: d.id })),
          );
        } else {
          setDepartmentOptions([]);
          setDepartmentsLoadFailed(true);
        }
        if (rolesRes.status === "fulfilled") {
          setRoleOptions(
            rolesRes.value.data.map((r) => ({
              label: r.roleName ?? r.id,
              value: r.id,
            })),
          );
        } else {
          setRoleOptions([]);
          setRolesLoadFailed(true);
        }
      })
      .finally(() => {
        setIsLoadingInitial(false);
      });
  }, [isOpen]);

  const buildPayload = (): AdminUserCreateBody => ({
    phoneNumber: normalizePhone(formData.phoneNumber.trim()) as string,
    firstName: formData.firstName.trim(),
    lastName: formData.lastName.trim(),
    email: formData.email.trim() || undefined,
    password: formData.password || undefined,
    identityNumber: formData.identityNumber.trim() || undefined,
    title: formData.title.trim() || undefined,
    sicilNo: formData.sicilNo.trim() || undefined,
    ldapUsername: formData.ldapUsername.trim() || undefined,
    locationId: formData.locationId || undefined,
    departmentId: formData.departmentId || undefined,
    roleId: formData.roleId || undefined,
    gender: formData.gender || undefined,
  });

  const submitStaff = async () => {
    const tc = formData.identityNumber.trim();
    const phone = formData.phoneNumber.trim();
    const tcRegex = /^[1-9]\d{10}$/;
    setShowValidation(true);
    if (!formData.firstName.trim() || !formData.lastName.trim()) {
      return;
    }
    if (!phone) {
      toast.error(
        t("staffManagement.addModal.phoneRequired"),
      );
      return;
    }
    if (!formData.departmentId) {
      toast.error(
        departmentsLoadFailed
          ? t("staffManagement.addModal.departmentLoadFailed")
          : t("staffManagement.addModal.departmentRequired"),
      );
      return;
    }
    if (!formData.roleId) {
      toast.error(
        rolesLoadFailed
          ? t("staffManagement.addModal.rolesLoadFailed")
          : t("staffManagement.addModal.rolesRequired"),
      );
      return;
    }
    if (tc && !tcRegex.test(tc)) {
      toast.error(
        t("staffManagement.addModal.tcInvalid"),
      );
      return;
    }
    if (!normalizePhone(phone)) {
      toast.error(
        t("staffManagement.addModal.phoneInvalid"),
      );
      return;
    }
    setIsSubmitting(true);
    try {
      await staffManagementApi.create(buildPayload());
      onSuccess?.();
      onClose();
    } catch (err: unknown) {
      const res = (
        err as {
          response?: {
            data?: {
              message?: string | string[];
              messages?: string[];
              errors?: string | string[];
            };
          };
        }
      )?.response?.data;
      const pickMessage = (
        v: string | string[] | undefined,
      ): string | undefined => {
        if (Array.isArray(v)) return v.length > 0 ? v.join(", ") : undefined;
        return typeof v === "string" && v.trim() ? v : undefined;
      };
      let message =
        pickMessage(res?.messages) ??
        pickMessage(res?.message) ??
        pickMessage(res?.errors);
      const validationTr: [RegExp | string, string][] = [
        [
          /phone\s*number.*(must be|required|valid)/i,
          t("staffManagement.addModal.phoneInvalidFull"),
        ],
        [/email.*(must be|invalid)/i, t("staffManagement.addModal.emailInvalidFull")],
        [/first\s*name.*(empty|required)/i, "Ad zorunludur."],
        [/last\s*name.*(empty|required)/i, "Soyad zorunludur."],
        [/phone\s*number.*empty/i, "Telefon zorunludur."],
        [/already exists|duplicate/i, t("staffManagement.addModal.duplicate")],
      ];
      if (message?.trim()) {
        const lower = message.toLowerCase();
        for (const [pattern, tr] of validationTr) {
          if (
            typeof pattern === "string"
              ? lower.includes(pattern)
              : pattern.test(message)
          ) {
            message = tr;
            break;
          }
        }
      }
      const msg =
        message ||
        t("staffManagement.addModal.errorGeneric");
      toast.error(
        message ? t("staffManagement.addModal.errorServer", { message }) : msg,
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const customHeader = (
    <>
      <div className={styles.modalHeader}>
        <div className={styles.modalHeaderLeft}>
          <AddEmployeeIcon width={24} height={24} color="#14181F" />
          <h3 className={styles.modalTitle}>
            {t("staffManagement.addModal.title")}
          </h3>
        </div>
        <button
          type="button"
          className={styles.closeButton}
          onClick={onClose}
          aria-label={t("common.close")}
        >
          <CloseIcon width={20} height={20} color="#72808f" />
        </button>
      </div>
      <div className={styles.stepTabs}>
        <div className={styles.stepTabWrapper}>
          <span
            className={`${styles.stepTab} ${currentStep === 0 ? styles.active : ""}`}
          >
            <span className={styles.stepNumber}>1</span>
            {t("staffManagement.addModal.step1")}
          </span>
        </div>
        <div className={styles.stepTabWrapper}>
          <span
            className={`${styles.stepTab} ${currentStep === 1 ? styles.active : ""}`}
          >
            <span className={styles.stepNumber}>2</span>
            {t("staffManagement.addModal.step2")}
          </span>
        </div>
      </div>
      <div className={styles.stepProgressFull}>
        <div
          className={styles.stepProgressFullBar}
          style={{ width: `${((currentStep + 1) / 2) * 100}%` }}
        />
      </div>
    </>
  );

  const step1Content = (
    <div className={styles.sectionedForm}>
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionBadge}>1</span>
          <div>
            <h4 className={styles.sectionTitle}>
              {t("staffManagement.addModal.sectionIdentity")}
            </h4>
            <p className={styles.sectionSubtitle}>
              {t("staffManagement.addModal.sectionIdentityHint")}
            </p>
          </div>
        </div>
        <div className={styles.grid2}>
          <Input
            label={t("staffManagement.addModal.firstName")}
            value={formData.firstName}
            maxLength={50}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, firstName: e.target.value }))
            }
            placeholder={t("staffManagement.addModal.firstNamePlaceholder")}
            required
          />
          <Input
            label={t("staffManagement.addModal.lastName")}
            value={formData.lastName}
            maxLength={50}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, lastName: e.target.value }))
            }
            placeholder={t("staffManagement.addModal.lastNamePlaceholder")}
            required
          />
          <Input
            label={t("staffManagement.addModal.identityNumber")}
            value={formData.identityNumber}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                identityNumber: e.target.value,
              }))
            }
            placeholder="12345678901"
          />
          <Dropdown
            label={t("staffManagement.addModal.gender")}
            placeholder={t("staffManagement.editModal.genderPlaceholder")}
            options={GENDER_OPTIONS}
            value={formData.gender ?? ""}
            selectedLabel={
              formData.gender
                ? GENDER_OPTIONS.find((o) => o.value === formData.gender)?.label
                : undefined
            }
            onSelect={(value) => {
              const raw = Array.isArray(value) ? value[0] : value;
              const normalized =
                raw === "male" || raw === "female" ? raw : null;
              setFormData((prev) => ({ ...prev, gender: normalized }));
            }}
          />
        </div>
      </section>

      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionBadge}>2</span>
          <div>
            <h4 className={styles.sectionTitle}>
              {t("staffManagement.addModal.sectionContact")}
            </h4>
            <p className={styles.sectionSubtitle}>
              {t("staffManagement.addModal.sectionContactHint")}
            </p>
          </div>
        </div>
        <div className={styles.grid2}>
          <Input
            label={t("staffManagement.addModal.phoneNumber")}
            value={formData.phoneNumber}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                phoneNumber: maskTurkishPhone(e.target.value),
              }))
            }
            placeholder="+90 5XX XXX XX XX"
            inputMode="tel"
            maxLength={17}
            required
          />
          <Input
            label={t("staffManagement.addModal.email")}
            type="email"
            value={formData.email}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, email: e.target.value }))
            }
            placeholder="user@example.com"
          />
        </div>
      </section>

      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionBadge}>3</span>
          <div>
            <h4 className={styles.sectionTitle}>
              {t("staffManagement.addModal.sectionAccount")}
            </h4>
            <p className={styles.sectionSubtitle}>
              {t("staffManagement.addModal.sectionAccountHint")}
            </p>
          </div>
        </div>
        <div className={styles.grid2}>
          <Input
            label={t("staffManagement.addModal.jobTitle")}
            value={formData.title}
            maxLength={100}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, title: e.target.value }))
            }
            placeholder={t("staffManagement.addModal.titlePlaceholder")}
          />
          <Input
            label={t("common.sicilNo")}
            value={formData.sicilNo}
            maxLength={50}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, sicilNo: e.target.value }))
            }
            placeholder={t("staffManagement.editModal.sicilNoPlaceholder")}
          />
          <Input
            label={t("staffManagement.addModal.ldapUsername")}
            value={formData.ldapUsername}
            maxLength={100}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                ldapUsername: e.target.value,
              }))
            }
            placeholder={t("staffManagement.addModal.ldapUsernamePlaceholder")}
          />
          <Input
            label={t("staffManagement.addModal.password")}
            type="password"
            value={formData.password}
            maxLength={50}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, password: e.target.value }))
            }
            placeholder="••••••••"
          />
        </div>
      </section>
    </div>
  );

  const step2Content = (
    <div className={styles.sectionedForm}>
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionBadge}>1</span>
          <div>
            <h4 className={styles.sectionTitle}>
              {t("staffManagement.addModal.sectionOrganization")}
            </h4>
            <p className={styles.sectionSubtitle}>
              {t("staffManagement.addModal.sectionOrganizationHint")}
            </p>
          </div>
        </div>
        <div className={styles.grid2}>
          <Dropdown
            label={`${t("staffManagement.addModal.department")} *`}
            placeholder={
              departmentsLoadFailed
                ? t("staffManagement.addModal.departmentLoadFailedShort")
                : t("staffManagement.filters.departmentPlaceholder")
            }
            searchable
            options={departmentOptions}
            value={formData.departmentId ?? ""}
            selectedLabel={
              departmentOptions.find((o) => o.value === formData.departmentId)
                ?.label
            }
            onSelect={(v) =>
              setFormData((prev) => ({
                ...prev,
                departmentId: v && typeof v === "string" ? v : null,
              }))
            }
            disabled={departmentsLoadFailed}
            error={
              (showValidation && !formData.departmentId) ||
              departmentsLoadFailed
            }
            errorMessage={
              departmentsLoadFailed
                ? t("staffManagement.addModal.departmentNoAccess")
                : showValidation && !formData.departmentId
                  ? t("staffManagement.addModal.departmentRequired")
                  : undefined
            }
          />
        </div>
      </section>

      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionBadge}>2</span>
          <div>
            <h4 className={styles.sectionTitle}>
              {t("staffManagement.addModal.sectionRole")}
            </h4>
            <p className={styles.sectionSubtitle}>
              {t("staffManagement.addModal.sectionRoleHint")}
            </p>
          </div>
        </div>
        <div className={styles.grid2}>
          <Dropdown
            label={`${t("staffManagement.addModal.role")} *`}
            placeholder={
              rolesLoadFailed
                ? t("staffManagement.addModal.rolesLoadFailedShort")
                : t("staffManagement.addModal.rolePlaceholder")
            }
            options={roleOptions}
            // Dropdown statik listede ilk 10 seçeneği gösterip gerisini
            // kaydırmayla açıyor; 10'dan fazla rol olunca Kursiyer gibi roller
            // görünmüyordu. Tüm liste gösterilir, arama kutusu eklenir.
            initialDisplayCount={Math.max(roleOptions.length, 10)}
            searchable
            value={formData.roleId ?? ""}
            selectedLabel={
              roleOptions.find((o) => o.value === formData.roleId)?.label
            }
            onSelect={(v) =>
              setFormData((prev) => ({
                ...prev,
                roleId: v && typeof v === "string" ? v : null,
              }))
            }
            disabled={rolesLoadFailed}
            error={
              (showValidation && !formData.roleId) || rolesLoadFailed
            }
            errorMessage={
              rolesLoadFailed
                ? t("staffManagement.addModal.rolesNoAccess")
                : showValidation && !formData.roleId
                  ? t("staffManagement.addModal.rolesRequired")
                  : undefined
            }
          />
        </div>
      </section>
    </div>
  );

  const customFooter = (
    <div className={styles.footerWrapper}>
      <div className={styles.footerButtons}>
        {currentStep === 0 ? (
          <Button
            variant="text"
            color="secondary"
            type="button"
            onClick={onClose}
          >
            {t("staffManagement.addModal.cancel")}
          </Button>
        ) : (
          <Button
            variant="text"
            color="secondary"
            type="button"
            onClick={() => setCurrentStep(0)}
          >
            {t("staffManagement.addModal.back")}
          </Button>
        )}
        {currentStep === 0 ? (
          <Button
            variant="contained"
            color="primary"
            type="button"
            onClick={() => {
              if (
                formData.firstName.trim() &&
                formData.lastName.trim() &&
                formData.phoneNumber.trim()
              ) {
                setCurrentStep(1);
              }
            }}
            disabled={
              isLoadingInitial ||
              !formData.firstName.trim() ||
              !formData.lastName.trim() ||
              !formData.phoneNumber.trim()
            }
          >
            {isLoadingInitial
              ? t("common.loading")
              : t("staffManagement.addModal.next")}
          </Button>
        ) : (
          <Button
            variant="contained"
            color="primary"
            type="button"
            onClick={() => submitStaff()}
            disabled={
              isSubmitting ||
              !formData.firstName.trim() ||
              !formData.lastName.trim() ||
              !formData.phoneNumber.trim()
            }
          >
            {isSubmitting
              ? t("common.loading")
              : t("staffManagement.addModal.submit")}
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t("staffManagement.addModal.title")}
      customHeader={customHeader}
      customFooter={customFooter}
      isMultiStep
      hideStepProgress
      currentStep={currentStep}
      stepContent={[step1Content, step2Content]}
      onStepChange={setCurrentStep}
      size="lg"
    >
      {null}
    </Modal>
  );
};

export default AddStaffModal;
